/**
 * Piezas comunes de los documentos imprimibles de la Declaración de Renta
 *
 * Todos los PDF de renta que genera TAXI (un módulo por año y régimen) siguen la misma
 * línea gráfica: membrete, antecedentes del contribuyente, secciones, nota legal y firmas,
 * con estos estilos. Cada módulo arma solo sus recuadros y sus notas, y llama a
 * buildDocumento.
 *
 * El documento se entrega al cliente para su revisión y firma, por lo que está diseñado
 * para ser impreso: sin colores ni fondos. La jerarquía se construye solo con tipografía
 * y líneas, siguiendo la convención contable (línea simple = subtotal, línea gruesa =
 * total, doble línea = resultado final del ejercicio).
 */
const { formatCurrency } = require('./formatUtils');

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

const fmt = (val) => {
  const result = formatCurrency(val);
  return result !== '' && result !== null && result !== undefined ? result : '0';
};

const fechaCorta = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};

const fechaLarga = () => {
  const d = new Date();
  return `${d.getDate()} de ${MESES[d.getMonth()]} de ${d.getFullYear()}`;
};

// -----------------------------------------------------------------------
// Helpers HTML
// -----------------------------------------------------------------------

const fieldRow = (sign, code, label, value, type = '') => {
  const cls = type ? ` row-${type}` : '';
  return `
    <div class="field-row${cls}">
      <span class="sign">${sign}</span>
      <span class="code">${code}</span>
      <span class="label">${label}</span>
      <span class="value">${value}</span>
    </div>`;
};

const subsectionHtml = (title, rows) => `
  <div class="subsection">
    <div class="subsection-title">${title}</div>
    ${rows.join('')}
  </div>`;

const sectionHtml = (numeral, title, subtitle, content, extraClass = '') => `
  <div class="section${extraClass ? ` ${extraClass}` : ''}">
    <div class="section-header">
      <div class="section-title">${numeral ? `${numeral}.&nbsp;&nbsp;` : ''}${title}</div>
      <div class="section-subtitle">${subtitle}</div>
    </div>
    <div class="section-body">${content}</div>
  </div>`;

const metaRow = (label, value) => `
  <div class="meta-row">
    <span class="meta-label">${label}</span>
    <span class="meta-value">${value}</span>
  </div>`;

// -----------------------------------------------------------------------
// Nota legal
// queSeDetermina: con qué empieza la nota, por ejemplo «La determinación de la base
// imponible y del Impuesto de Primera Categoría»; articulo: el del régimen, por ejemplo
// «artículo 14 letra D) N° 3».
// -----------------------------------------------------------------------
const buildNotaLegal = (company, { anioTributario, anioComercial, queSeDetermina, articulo }) => {
  const contribuyente = company.name ? `de ${company.name}` : 'del contribuyente';
  return `
  <div class="legal-note">
    <div class="block-title">Nota sobre la preparación y uso de este documento</div>
    <p>${queSeDetermina} contenida en el presente
    documento, correspondiente al Año Tributario ${anioTributario} (año comercial ${anioComercial}), ha sido elaborada
    exclusivamente sobre la base de los antecedentes contables, tributarios y documentarios proporcionados
    por el representante legal ${contribuyente}, quien declara y garantiza su integridad, exactitud y veracidad.</p>
    <p>El trabajo realizado consistió en la aplicación de las disposiciones de la Ley sobre Impuesto a la Renta,
    en particular del régimen establecido en su ${articulo}, a la información recibida. No constituye
    una auditoría, revisión ni procedimiento de aseguramiento conforme a normas de auditoría generalmente
    aceptadas, por lo que no se emite opinión alguna sobre los estados financieros del contribuyente ni sobre
    la información que sirvió de base para esta determinación.</p>
    <p>Toda modificación, omisión o corrección posterior de los antecedentes entregados podrá alterar las cifras
    aquí determinadas, siendo responsabilidad del contribuyente informar oportunamente tales situaciones.</p>
    <p>Este documento contiene información privada, confidencial y de uso restringido del contribuyente y de su
    representante legal. Su reproducción, distribución o divulgación a terceros, total o parcial, requiere
    autorización previa y por escrito.</p>
  </div>`;
};

// -----------------------------------------------------------------------
// Firmas
// -----------------------------------------------------------------------
const buildFirmas = (company, anioTributario) => {
  const lugar = company.comuna || 'Santiago';
  return `
  <div class="firmas-section">
    <div class="block-title">Aprobación y firma</div>
    <p class="firma-intro">El representante legal declara haber revisado los antecedentes entregados y las cifras
    contenidas en el presente documento, aprobándolas en todas sus partes para efectos de la presentación del
    Formulario 22 correspondiente al Año Tributario ${anioTributario}.</p>
    <p class="firma-lugar">En ${lugar}, a ${fechaLarga()}.</p>
    <div class="firmas-row">
      <div class="firma-box">
        <div class="firma-space"></div>
        <div class="firma-role">Contador</div>
        <div class="firma-meta">Nombre y RUT</div>
      </div>
      <div class="firma-box">
        <div class="firma-space"></div>
        <div class="firma-role">Representante Legal</div>
        <div class="firma-meta">${company.rep_legal || 'Nombre y RUT'}${company.rut_representante ? ` · RUT ${company.rut_representante}` : ''}</div>
      </div>
    </div>
  </div>`;
};

// -----------------------------------------------------------------------
// CSS: documento en blanco y negro, pensado para impresión y firma.
// -----------------------------------------------------------------------
const CSS = `
  * { box-sizing: border-box; margin: 0; padding: 0; }

  /* El documento se imprime: se fuerza esquema claro para que el render no
     dependa del tema del navegador o del entorno que genera el PDF. */
  html { color-scheme: light; }

  body {
    font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
    font-size: 10.5px;
    line-height: 1.45;
    color: #111;
    background: #fff;
    padding: 4px 2px;
  }

  /* --- Membrete --- */
  .letterhead {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    padding-bottom: 7px;
    border-bottom: 1.5px solid #111;
  }
  .letterhead-entity {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
  }
  .letterhead-sub {
    font-size: 9px;
    color: #555;
    margin-top: 3px;
    letter-spacing: 0.03em;
  }
  .letterhead-doc {
    text-align: right;
    font-size: 8.5px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: #555;
    white-space: nowrap;
  }

  /* --- Título del documento --- */
  .doc-title-block {
    margin: 26px 0 24px;
    text-align: center;
  }
  .doc-title {
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }
  .doc-subtitle {
    font-size: 10px;
    color: #444;
    margin-top: 5px;
    letter-spacing: 0.02em;
  }

  /* --- Bloques con encabezado --- */
  .block-title {
    font-size: 8.5px;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #111;
    padding-bottom: 5px;
    border-bottom: 0.75px solid #111;
    margin-bottom: 9px;
  }

  /* --- Antecedentes del contribuyente --- */
  .meta-block { margin-bottom: 26px; }
  .meta-grid {
    display: flex;
    flex-wrap: wrap;
  }
  .meta-row {
    display: flex;
    width: 50%;
    padding: 2.5px 16px 2.5px 0;
    font-size: 9.5px;
  }
  .meta-label {
    width: 116px;
    color: #666;
    flex-shrink: 0;
  }
  .meta-value { flex: 1; color: #111; }

  /* --- Secciones --- */
  .section {
    margin-bottom: 22px;
    page-break-inside: avoid;
  }
  .section-header {
    border-bottom: 0.75px solid #111;
    padding-bottom: 5px;
    margin-bottom: 9px;
  }
  /* Sección larga que sigue en la página siguiente en vez de saltar entera */
  .section.section-continua { page-break-inside: auto; }
  .section-continua .section-header,
  .section-continua .subsection-title { page-break-after: avoid; }
  .section-continua .field-row,
  .section-continua tr { page-break-inside: avoid; }
  .section-title {
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }
  .section-subtitle {
    font-size: 9px;
    color: #666;
    margin-top: 2px;
  }
  .section-body { padding: 0 2px; }

  .subsection { margin-bottom: 13px; }
  .subsection:last-child { margin-bottom: 0; }
  .subsection-title {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #444;
    margin-bottom: 4px;
  }

  /* --- Filas de detalle --- */
  .field-row {
    display: flex;
    align-items: baseline;
    padding: 2.5px 0;
    gap: 8px;
  }
  .sign {
    width: 22px;
    font-size: 9px;
    color: #777;
    text-align: center;
    flex-shrink: 0;
  }
  .code {
    width: 34px;
    font-size: 8.5px;
    color: #777;
    text-align: center;
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
  }
  .label {
    flex: 1;
    font-size: 10px;
    color: #111;
  }
  .value {
    width: 118px;
    text-align: right;
    font-size: 10px;
    font-variant-numeric: tabular-nums;
    flex-shrink: 0;
  }

  /* Jerarquía por líneas, no por color (convención contable):
     subtotal = línea simple, total = línea gruesa,
     resultado final = línea gruesa arriba y doble línea abajo. */
  .field-row.row-subtotal {
    border-top: 0.5px solid #999;
    margin-top: 3px;
    padding-top: 4px;
  }
  .field-row.row-subtotal .label,
  .field-row.row-subtotal .value { font-weight: 600; }

  .field-row.row-highlight .label,
  .field-row.row-highlight .value { font-weight: 600; }

  .field-row.row-total {
    border-top: 1px solid #111;
    margin-top: 4px;
    padding-top: 5px;
  }
  .field-row.row-total .label,
  .field-row.row-total .value { font-weight: 700; }

  .field-row.row-tax-result {
    border-top: 1px solid #111;
    border-bottom: 3px double #111;
    margin-top: 6px;
    padding: 6px 0;
  }
  .field-row.row-tax-result .label {
    font-weight: 700;
    letter-spacing: 0.02em;
  }
  .field-row.row-tax-result .value {
    font-weight: 700;
    font-size: 12px;
  }

  /* --- Tablas de registros (R20 / R21) --- */
  .data-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 9px;
  }
  .data-table th {
    font-size: 8px;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: #444;
    padding: 5px 6px;
    text-align: right;
    border-bottom: 0.75px solid #111;
    white-space: nowrap;
  }
  .data-table td {
    padding: 4px 6px;
    text-align: right;
    font-variant-numeric: tabular-nums;
    border-bottom: 0.4px solid #ddd;
  }
  .data-table .td-label {
    text-align: left;
    width: 40%;
  }
  .data-table .td-code {
    text-align: center;
    font-size: 8px;
    color: #777;
    white-space: nowrap;
  }
  .data-table tr.row-total td {
    border-top: 1px solid #111;
    border-bottom: none;
    font-weight: 700;
    padding-top: 5px;
  }

  /* --- Resultado del ejercicio (cierre enmarcado, sin relleno) --- */
  .section-final {
    border: 1px solid #111;
    padding: 14px 16px 12px;
    margin-top: 28px;
  }
  .section-final .section-header {
    border-bottom: 0.5px solid #999;
  }

  /* --- Notas de la declaración y nota legal --- */
  .notas-block { margin-top: 30px; }
  .nota-item {
    margin-bottom: 13px;
    page-break-inside: avoid;
  }
  .nota-item:last-child { margin-bottom: 0; }
  .nota-title {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.03em;
    color: #111;
    margin-bottom: 4px;
  }

  .legal-note {
    margin-top: 30px;
    page-break-inside: avoid;
  }

  .notas-block p,
  .legal-note p {
    font-size: 8.5px;
    line-height: 1.6;
    color: #333;
    text-align: justify;
    margin-bottom: 7px;
  }
  .notas-block p:last-child,
  .legal-note p:last-child { margin-bottom: 0; }

  /* --- Firmas --- */
  .firmas-section {
    margin-top: 30px;
    page-break-inside: avoid;
  }
  .firma-intro {
    font-size: 8.5px;
    line-height: 1.6;
    color: #333;
    text-align: justify;
    margin-bottom: 10px;
  }
  .firma-lugar {
    font-size: 9px;
    color: #333;
    margin-bottom: 8px;
  }
  .firmas-row {
    display: flex;
    justify-content: space-between;
    gap: 60px;
    margin-top: 34px;
  }
  .firma-box {
    flex: 1;
    text-align: center;
  }
  .firma-space {
    height: 46px;
    border-bottom: 0.75px solid #111;
    margin-bottom: 6px;
  }
  .firma-role {
    font-size: 9.5px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .firma-meta {
    font-size: 8.5px;
    color: #666;
    margin-top: 2px;
  }

  @media print {
    body { padding: 0; }
    .section { page-break-inside: avoid; }
  }
`;

// -----------------------------------------------------------------------
// Documento completo: membrete, título, antecedentes del contribuyente, los
// recuadros del módulo, sus notas, la nota legal y las firmas.
//
// opciones:
//   anioTributario, anioComercial
//   regimen      — texto de «Régimen tributario» en los antecedentes
//   formulario   — texto de «Formulario», por ejemplo «N° 22 — Recuadros N° 17 a 21»
//   subtitulos   — líneas bajo el título «Declaración de Renta»
//   secciones    — HTML de los recuadros (sectionHtml)
//   notas        — HTML de las notas para la presentación del F22
//   notaLegal    — { queSeDetermina, articulo } para buildNotaLegal
// -----------------------------------------------------------------------
const buildDocumento = (company, opciones) => {
  const { anioTributario, anioComercial, regimen, formulario, subtitulos, secciones, notas, notaLegal } = opciones;
  const direccion = [company.address, company.comuna, company.region].filter(Boolean).join(', ');
  const representante = company.rep_legal
    ? `${company.rep_legal}${company.rut_representante ? ` · RUT ${company.rut_representante}` : ''}`
    : '—';

  const antecedentes = `
  <div class="meta-block">
    <div class="block-title">Antecedentes del contribuyente</div>
    <div class="meta-grid">
      ${metaRow('Razón social', company.name || '—')}
      ${metaRow('RUT', company.rut || '—')}
      ${metaRow('Domicilio', direccion || '—')}
      ${metaRow('Representante legal', representante)}
      ${metaRow('Régimen tributario', regimen)}
      ${metaRow('Año tributario', `${anioTributario} (comercial ${anioComercial})`)}
      ${metaRow('Formulario', formulario)}
      ${metaRow('Fecha de emisión', fechaCorta())}
    </div>
  </div>`;

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8" />
  <title>Declaración de Renta AT ${anioTributario} — ${company.name || 'Contribuyente'}</title>
  <style>${CSS}</style>
</head>
<body>
  <div class="letterhead">
    <div>
      <div class="letterhead-entity">${company.name || 'Contribuyente'}</div>
      <div class="letterhead-sub">${company.rut ? `RUT ${company.rut}` : ''}${direccion ? ` · ${direccion}` : ''}</div>
    </div>
    <div class="letterhead-doc">
      Formulario 22<br />Año Tributario ${anioTributario}
    </div>
  </div>

  <div class="doc-title-block">
    <div class="doc-title">Declaración de Renta</div>
    <div class="doc-subtitle">
      ${subtitulos.join('<br />\n      ')}
    </div>
  </div>

  ${antecedentes}
  ${secciones}
  ${notas}
  ${buildNotaLegal(company, { anioTributario, anioComercial, ...notaLegal })}
  ${buildFirmas(company, anioTributario)}
</body>
</html>`;
};

// -----------------------------------------------------------------------
// Exportación vía impresión del navegador (se mantiene por compatibilidad)
// -----------------------------------------------------------------------
const abrirParaImprimir = (html) => {
  const win = window.open('', '_blank');
  if (!win) {
    alert('No se pudo abrir la ventana de impresión. Verifique que el bloqueador de ventanas emergentes esté desactivado.');
    return;
  }
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 400);
};

module.exports = {
  fmt,
  fieldRow,
  subsectionHtml,
  sectionHtml,
  metaRow,
  buildDocumento,
  abrirParaImprimir,
  // Para otros documentos con la misma línea gráfica (Certificado N° 69 de la DJ 1947)
  CSS,
  fechaLarga,
};
